import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import ConfirmPopup, {
  type ConfirmPopupProps,
  type ConfirmTone,
} from "./ConfirmPopup";

export type ConfirmOptions = {
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: ConfirmTone;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<void>;

const ConfirmPopupContext = createContext<ConfirmFn | null>(null);

export const ConfirmPopupProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolveRef = useRef<(() => void) | null>(null);
  const rejectRef = useRef<(() => void) | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    rejectRef.current?.();
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    resolveRef.current?.();
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  const confirm = useCallback<ConfirmFn>((nextOptions) => {
    setOptions(nextOptions);
    setOpen(true);

    return new Promise<void>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  }, []);

  const popupProps: ConfirmPopupProps = useMemo(
    () => ({
      open,
      onClose: close,
      onConfirm: handleConfirm,
      title: options.title ?? "Are you sure?",
      description: options.description,
      children: options.children,
      confirmLabel: options.confirmLabel,
      cancelLabel: options.cancelLabel,
      confirmTone: options.confirmTone,
    }),
    [close, handleConfirm, open, options]
  );

  return (
    <ConfirmPopupContext.Provider value={confirm}>
      {children}
      <ConfirmPopup {...popupProps} />
    </ConfirmPopupContext.Provider>
  );
};

export const useConfirmPopup = (): ConfirmFn => {
  const confirm = useContext(ConfirmPopupContext);
  if (!confirm) {
    throw new Error("useConfirmPopup must be used within ConfirmPopupProvider");
  }
  return confirm;
};
